import React, { useEffect, useState } from "react";
import { Download, FileText, Printer } from "lucide-react";
import api, { API } from "@/lib/api";
import { PageHeader, Card, SectionTitle, Empty } from "@/components/ui/page";
import { Pill } from "@/components/StatusBadge";
import { fmtDuration } from "@/lib/format";

const REPORTS = [
  { key: "breaks", label: "Break Report" },
  { key: "exceptions", label: "Break Exception Report" },
  { key: "attendance", label: "Attendance Report" },
];

export default function Reports() {
  const [tab, setTab] = useState("breaks");
  const [rows, setRows] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [proc, setProc] = useState("");
  const [bt, setBt] = useState("");
  const [sod, setSod] = useState(null);
  const [eod, setEod] = useState(null);

  useEffect(() => { api.get("/processes").then((r) => setProcesses(r.data)); }, []);
  useEffect(() => {
    api.get("/reports/sod").then((r) => setSod(r.data)).catch(() => {});
    api.get("/reports/eod").then((r) => setEod(r.data)).catch(() => {});
  }, []);

  const load = () => {
    if (tab === "exceptions") { api.get("/reports/exceptions").then((r) => setRows(r.data)); return; }
    if (tab === "attendance") { api.get("/attendance").then((r) => setRows(r.data)); return; }
    const params = {};
    if (start) params.start = start; if (end) params.end = end; if (proc) params.process = proc; if (bt) params.break_type = bt;
    api.get("/reports/breaks", { params }).then((r) => setRows(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const exportFile = (report, fmt) => {
    const token = localStorage.getItem("gs_token");
    fetch(`${API}/export/${report}?fmt=${fmt}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a");
        const base = report === "breaks" ? "Break_Report" : report === "attendance" ? "Attendance_Report" : "Workforce_Report";
        a.href = url; a.download = `GoSource_${base}_${new Date().toISOString().slice(0, 10)}.${fmt === "xlsx" ? "xlsx" : "csv"}`; a.click();
        URL.revokeObjectURL(url);
      });
  };

  const exportReport = tab === "exceptions" ? "breaks" : tab;

  return (
    <div>
      <PageHeader title="Reports" subtitle="Filter, view and export workforce data" testId="reports-header">
        <button data-testid="report-csv" onClick={() => exportFile(exportReport, "csv")} className="flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50"><Download className="h-3.5 w-3.5" /> CSV</button>
        <button data-testid="report-xlsx" onClick={() => exportFile(exportReport, "xlsx")} className="flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50"><Download className="h-3.5 w-3.5" /> Excel</button>
        <button data-testid="report-print" onClick={() => window.print()} className="flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50"><Printer className="h-3.5 w-3.5" /> Print</button>
      </PageHeader>

      {/* SOD / EOD summary */}
      <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="p-5" testId="sod-card">
          <SectionTitle>SOD Report · {sod?.date}</SectionTitle>
          {sod && <div className="mt-3 grid grid-cols-4 gap-3 text-center">
            {[["Total HC", sod.total_hc], ["Present", sod.present], ["Absent", sod.absent], ["Working", sod.working], ["Break", sod.break], ["Available", sod.available_hc], ["Exceptions", sod.exceptions], ["Expected", sod.expected_hc]].map(([l, v]) => (
              <div key={l} className="rounded-lg bg-slate-50 p-2"><p className="font-display text-xl font-extrabold text-[#0B1F33]">{v}</p><p className="text-[10px] text-slate-500">{l}</p></div>
            ))}
          </div>}
        </Card>
        <Card className="p-5" testId="eod-card">
          <SectionTitle>EOD Report · {eod?.date}</SectionTitle>
          {eod && <div className="mt-3 grid grid-cols-4 gap-3 text-center">
            {[["Attendance", `${eod.attendance_pct}%`], ["Work Hrs", eod.total_work_hours], ["Break Hrs", eod.total_break_hours], ["Avg Break", `${eod.avg_break_min}m`], ["Exceptions", eod.break_exceptions], ["Total HC", eod.total_hc]].map(([l, v]) => (
              <div key={l} className="rounded-lg bg-slate-50 p-2"><p className="font-display text-xl font-extrabold text-[#0B1F33]">{v}</p><p className="text-[10px] text-slate-500">{l}</p></div>
            ))}
          </div>}
        </Card>
      </div>

      {/* Tabs */}
      <div className="mb-4 flex flex-wrap gap-2">
        {REPORTS.map((r) => (
          <button key={r.key} data-testid={`report-tab-${r.key}`} onClick={() => setTab(r.key)} className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${tab === r.key ? "bg-[#0B1F33] text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>{r.label}</button>
        ))}
      </div>

      {tab === "breaks" && (
        <Card className="mb-4 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <input data-testid="report-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            <input data-testid="report-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            <select data-testid="report-process" value={proc} onChange={(e) => setProc(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm"><option value="">All Processes</option>{processes.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}</select>
            <select data-testid="report-breaktype" value={bt} onChange={(e) => setBt(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm"><option value="">All Break Types</option><option value="short">Short Break</option><option value="lunch">Lunch</option><option value="personal">Personal</option><option value="meeting">Meeting / Training</option></select>
            <button data-testid="report-apply" onClick={load} className="rounded-lg bg-[#08A7A0] px-4 py-2 text-sm font-bold text-white">Apply</button>
          </div>
        </Card>
      )}

      <Card testId="report-table">
        <div className="overflow-x-auto">
          {tab === "attendance" ? (
            <table className="w-full text-sm">
              <thead className="bg-slate-50"><tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="px-4 py-3">Employee</th><th className="px-4 py-3">Date</th><th className="px-4 py-3">Status</th></tr></thead>
              <tbody>{rows.length === 0 && <tr><td colSpan={3}><Empty /></td></tr>}
                {rows.map((r) => <tr key={r.id} className="border-t border-slate-100"><td className="px-4 py-3 font-semibold text-[#0B1F33]">{r.name} <span className="text-xs text-slate-400">{r.employee_id}</span></td><td className="px-4 py-3">{r.date}</td><td className="px-4 py-3"><Pill tone={r.status === "present" ? "green" : "red"}>{r.status}</Pill></td></tr>)}
              </tbody>
            </table>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50"><tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="px-4 py-3">Employee</th><th className="px-4 py-3">Process</th><th className="px-4 py-3">Break</th>
                <th className="px-4 py-3">Duration</th><th className="px-4 py-3">Allowed</th><th className="px-4 py-3">Exceeded</th>
                <th className="px-4 py-3">Date</th><th className="px-4 py-3">Status</th></tr></thead>
              <tbody>{rows.length === 0 && <tr><td colSpan={8}><Empty /></td></tr>}
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`report-row-${r.id}`} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 font-semibold text-[#0B1F33]">{r.employee_name} <span className="text-xs text-slate-400">{r.employee_id}</span></td>
                    <td className="px-4 py-3 text-slate-600">{r.process}</td>
                    <td className="px-4 py-3">{r.break_label}</td>
                    <td className="px-4 py-3 font-mono text-xs">{fmtDuration(r.duration_seconds)}</td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-400">{fmtDuration(r.allowed_seconds)}</td>
                    <td className="px-4 py-3 font-mono text-xs">{r.exceeded_seconds > 0 ? <span className="font-bold text-red-600">+{fmtDuration(r.exceeded_seconds)}</span> : "—"}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{r.date}</td>
                    <td className="px-4 py-3">{r.status === "excess" ? <Pill tone="red">Excess</Pill> : <Pill tone="green">Within</Pill>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>
    </div>
  );
}
